import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import AxiosAdapter from './AxiosAdapter';
import axios from '../app/axiosInterceptor';
import {
  HTTP_GETTYPE,
  MSG_TIMEOUT_REQUEST,
  TIMEOUT_DEFAULT,
  STATUS_API,
  messageToastType_const
} from '../app/constant/config';
import { MESSAGE } from 'src/app/constant/message';
import { showGlobalToast } from './uiSlice';

export const getListAgencyBusiness = AxiosAdapter.GetHttp(
  'agencySlice/getListAgencyBusiness',
  '/agencies/business',
  HTTP_GETTYPE.ALL
);

export const getListAgencies = AxiosAdapter.GetHttp(
  'agencySlice/getListAgencies',
  '/agencies',
  HTTP_GETTYPE.ALL_PAGINATION
);

export const getListAgenciesByProvince = AxiosAdapter.GetHttp(
  'agencySlice/getListAgenciesByProvince',
  '/agencies',
  HTTP_GETTYPE.ALL_PAGINATION
);

export const createAgency = AxiosAdapter.HttpPost(
  'agencySlice/createAgency',
  '/agencies',
  MESSAGE.CREATE_AGENCY_SUCCESS,
  'Tạo mới đại lý thất bại'
);

export const createEndUserAgency = AxiosAdapter.HttpPost(
  'agencySlice/createEndUserAgency',
  '/agencies/create/enduser',
  MESSAGE.CREATE_AGENCY_SUCCESS,
  'Tạo mới thất bại!'
);

export const updateAgency = AxiosAdapter.HttpUpdateById(
  'agencySlice/updateAgency',
  '/agencies',
  MESSAGE.UPDATE_AGENCY_SUCCESS,
  'Cập nhật đại lý thất bại'
);

export const updateWarehouseTransfer = AxiosAdapter.HttpUpdate(
  'agencySlice/updateWarehouseTransfer',
  '/devices/transfer/agency',
  MESSAGE.WAREHOUSE_TRANSFER_SUCCESS,
  'Chuyển thiết bị xuống đại lý thất bại'
)

export const updateAgencyStatus = createAsyncThunk(
  'agencySlice/updateAgencyStatus',
  (payload, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      axios({
        url: process.env.REACT_APP_BACKEND_URL + `/agencies/${payload.id}/status`,
        method: 'PUT',
        headers: {
          'Access-Control-Allow-Origin': true,
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: 'Bearer ' + localStorage.getItem('access-token')
        },
        data: payload.data,
        timeout: TIMEOUT_DEFAULT
      })
        .then(res => resolve(res.data))
        .catch(err => {
          if (err.code === 'ECONNABORTED') reject(MSG_TIMEOUT_REQUEST);
          if (!err.response) reject(err);
          reject(rejectWithValue(err.response?.data));
        });
    });
  });

const AGENCY_CONFIG_LABELS = {
  allow_edit_customer: 'quyền sửa thông tin khách hàng',
  is_allow_swap: 'quyền đổi/thay thế thiết bị',
  is_allow_swap_sub_agency: 'quyền chuyển kho giữa các đại lý cấp 2'
};

// Lấy cấu hình (các cờ quyền hạn) của đại lý: { allow_edit_customer, is_allow_swap, is_allow_swap_sub_agency }
export const getAgencyConfig = createAsyncThunk(
  'agencySlice/getAgencyConfig',
  async (agencyId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/agencies/${agencyId}/config`);
      return { agencyId, ...res.data?.payload };
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err?.data?.message || 'Lấy cấu hình đại lý thất bại';
      return rejectWithValue({ message });
    }
  }
);

// Admin cập nhật cấu hình đại lý (partial): chỉ gửi cờ cần đổi, cờ không gửi giữ nguyên.
export const updateAgencyConfig = createAsyncThunk(
  'agencySlice/updateAgencyConfig',
  async ({ agencyId, config }, { dispatch, rejectWithValue }) => {
    const keys = Object.keys(config || {});
    const label =
      keys.length === 1 ? AGENCY_CONFIG_LABELS[keys[0]] : 'cấu hình đại lý';
    try {
      const res = await axios.put(`/agencies/${agencyId}/config`, config);
      const message =
        keys.length === 1
          ? `Đã ${config[keys[0]] ? 'bật' : 'tắt'} ${label} của đại lý`
          : 'Đã cập nhật cấu hình đại lý';
      dispatch(
        showGlobalToast({ message, type: messageToastType_const.success })
      );
      return { agencyId, ...res.data?.payload };
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err?.data?.message || `Cập nhật ${label} thất bại`;
      dispatch(
        showGlobalToast({ message, type: messageToastType_const.error })
      );
      return rejectWithValue({ message });
    }
  }
);

export const deleteAgency = AxiosAdapter.HttpDelete(
  'agencySlice/deleteAgency',
  'agencies',
  MESSAGE.DELETE_AGENCY_SUCCESS,
  'Xóa đại lý thất bại'
);

export const deleteMultiAgency = AxiosAdapter.HttpDeleteMulti(
  'agencySlice/deleteMultiAgency',
  'agencies/delete/agencies',
  MESSAGE.DELETE_AGENCY_SUCCESS,
  'Xóa đại lý thất bại'
);

// Ghi các cờ cấu hình trả về từ /config vào đại lý tương ứng trong cây listAgencies
const patchAgencyConfig = (list, payload) => {
  const { agencyId, ...config } = payload || {};
  (list || []).forEach(agency => {
    if (agency.id === agencyId) {
      Object.keys(config).forEach(key => {
        agency[key] = Boolean(config[key]);
      });
    }
    patchAgencyConfig(agency.children, payload);
  });
};

export const agencySlice = createSlice({
  name: 'agencySlice',
  initialState: {
    listAgencies: [],
    listAgenciesByProvince: [],
    listAgencyBusiness: [],
    totalAgencies: 0,
    totalAgenciesByProvince: 0,
    error: null,
    statusGet: null,
    statusGetByProvince: null,
    statusCreate: null,
    statusUpdate: null,
    statusDelete: null,
    statusDeleteMulti: null,
    statusTransfer: null,
    isLoading: false
  },

  reducers: {
    resetChange: state => {
      state.statusGet = null;
      state.statusCreate = null;
      state.statusUpdate = null;
      state.statusTransfer = null;
      state.statusDelete = null;
      state.isLoading = false;
      state.statusDeleteMulti = null;
      state.error = null;
    },
    resetListAgencies: state => {
      state.listAgencies = [];
      state.totalAgencies = 0;
      state.statusGet = null;
    }
  },

  extraReducers: {
    [getListAgencies.pending]: state => {
      state.statusGet = STATUS_API.PENDING;
      state.isLoading = true;
    },
    [getListAgencies.fulfilled]: (state, action) => {
      state.statusGet = STATUS_API.SUCCESS;
      state.listAgencies = action.payload.payload.data || action.payload.payload.agencies || [];
      state.totalAgencies = action.payload.payload.numberOfItem ?? 0;
      state.isLoading = false;
    },
    [getListAgencies.rejected]: (state, action) => {
      state.statusGetAllAgencies = STATUS_API.ERROR;
      state.error = action.payload?.message || action.error;
      state.isLoading = false;
    },
    [getListAgenciesByProvince.pending]: state => {
      state.statusGetByProvince = STATUS_API.PENDING;
    },
    [getListAgenciesByProvince.fulfilled]: (state, action) => {
      state.statusGetByProvince = STATUS_API.SUCCESS;
      state.listAgenciesByProvince =
        action.payload.payload.data || action.payload.payload.agencies || [];
      state.totalAgenciesByProvince = action.payload.payload.numberOfItem ?? 0;
    },
    [getListAgenciesByProvince.rejected]: (state, action) => {
      state.statusGetByProvince = STATUS_API.ERROR;
      state.error = action.payload?.message || action.error;
    },
    [createAgency.pending]: state => {
      state.statusCreate = STATUS_API.PENDING;
    },
    [createAgency.fulfilled]: (state, action) => {
      state.statusCreate = STATUS_API.SUCCESS;
    },
    [createAgency.rejected]: (state, action) => {
      state.statusCreate = STATUS_API.ERROR;
      state.error = action.payload?.message || action.error;
    },
    [createEndUserAgency.pending]: state => {
      state.statusCreate = STATUS_API.PENDING;
    },
    [createEndUserAgency.fulfilled]: (state, action) => {
      state.statusCreate = STATUS_API.SUCCESS;
    },
    [createEndUserAgency.rejected]: (state, action) => {
      state.statusCreate = STATUS_API.ERROR;
      state.error = action.payload?.message || action.error;
    },
    [updateAgency.pending]: state => {
      state.statusUpdate = STATUS_API.PENDING;
    },
    [updateAgency.fulfilled]: (state, action) => {
      state.statusUpdate = STATUS_API.SUCCESS;
    },
    [updateAgency.rejected]: (state, action) => {
      state.statusUpdate = STATUS_API.ERROR;
      state.error = action.payload?.message || action.error;
    },
    [updateWarehouseTransfer.pending]: state => {
      state.statusTransfer = STATUS_API.PENDING;
    },
    [updateWarehouseTransfer.fulfilled]: (state, action) => {
      state.statusTransfer = STATUS_API.SUCCESS;
    },
    [updateWarehouseTransfer.rejected]: (state, action) => {
      state.statusTransfer = STATUS_API.ERROR;
      state.error = action.payload?.message || action.error;
    },

    [getListAgencyBusiness.pending]: state => {
      state.statusGet = STATUS_API.PENDING;
      state.listAgencyBusiness = null;
      state.error = null;
    },
    [getListAgencyBusiness.fulfilled]: (state, action) => {
      state.statusGet = STATUS_API.SUCCESS;
      state.listAgencyBusiness = action.payload.payload.businesses;
      state.error = null;
    },
    [getListAgencyBusiness.rejected]: (state, action) => {
      state.statusGet = STATUS_API.ERROR;
      state.error = action.payload?.message || action.error;
    },
    [getAgencyConfig.fulfilled]: (state, action) => {
      patchAgencyConfig(state.listAgencies, action.payload);
    },
    [updateAgencyConfig.fulfilled]: (state, action) => {
      patchAgencyConfig(state.listAgencies, action.payload);
    },
    [deleteAgency.pending]: state => {
      state.statusDelete = STATUS_API.PENDING;
    },
    [deleteAgency.fulfilled]: (state, action) => {
      state.statusDelete = STATUS_API.SUCCESS;
    },
    [deleteAgency.rejected]: (state, action) => {
      state.statusDelete = STATUS_API.ERROR;
      state.errors = action.payload.message;
    },
    [deleteMultiAgency.pending]: state => {
      state.statusDeleteMulti = STATUS_API.PENDING;
    },
    [deleteMultiAgency.fulfilled]: (state, action) => {
      state.statusDeleteMulti = STATUS_API.SUCCESS;
    },
    [deleteMultiAgency.rejected]: (state, action) => {
      state.statusDeleteMulti = STATUS_API.ERROR;
      state.error = action.payload.message;
    },
  }
});

export const { resetChange, resetListAgencies } = agencySlice.actions;

export default agencySlice.reducer;
